import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiQuery } from '../use-api.js';
import { StatusPill } from '../components/StatusPill.js';
import { ACTIVE_STATUSES } from '../lib/status.js';
import type { RunsResponse, Run } from '../lib/types.js';

const STATUS_OPTIONS: string[] = [
  'QUEUED',
  'PROVISIONING',
  'RUNNING',
  'SUCCESS',
  'COMPLETED_NO_PR',
  'NEEDS_ITERATION',
  'FAILED',
  'TIMED_OUT',
  'CANCELLED',
];

function buildPath(status: string, limit: number): string {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  params.set('limit', String(limit));
  return `/api/runs?${params.toString()}`;
}

export function Runs() {
  const [status, setStatus] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [limit, setLimit] = useState(50);

  const path = buildPath(status, limit);
  const query = useApiQuery<RunsResponse>(['runs', status, limit], path, {
    // Poll every 5s while any run in the response is still in flight; otherwise
    // stop. React Query calls this with the current query on each tick.
    refetchInterval: (q) => {
      const rows = q.state.data?.runs ?? [];
      return rows.some((r) => ACTIVE_STATUSES.has(r.status)) ? 5000 : false;
    },
  });

  const allRuns = query.data?.runs ?? [];
  const runs = activeOnly ? allRuns.filter((r) => ACTIVE_STATUSES.has(r.status)) : allRuns;

  return (
    <section>
      <h2>Runs</h2>
      <div className="toolbar">
        <label>
          Status:{' '}
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Status filter"
          >
            <option value="">All</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label>
          Limit:{' '}
          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} aria-label="Limit">
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
        <label>
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />{' '}
          Active only
        </label>
      </div>

      {query.isLoading && <p>Loading runs…</p>}
      {query.isError && <p className="error">Failed to load runs.</p>}

      {!query.isLoading && !query.isError && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Run ID</th>
              <th>Repo</th>
              <th>User</th>
              <th>Status</th>
              <th>Created</th>
              <th>Turns</th>
              <th>PR</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && (
              <tr><td colSpan={7} className="empty">No runs.</td></tr>
            )}
            {runs.map((run: Run) => (
              <tr key={run.run_id}>
                <td><Link to={`/runs/${encodeURIComponent(run.run_id)}`}>{run.run_id}</Link></td>
                <td>{run.repo ?? '—'}</td>
                <td>{run.user_id ?? '—'}</td>
                <td><StatusPill status={run.status} /></td>
                <td>{run.created_at ? run.created_at.slice(0, 16) : '—'}</td>
                <td>
                  {run.num_turns != null && run.max_turns != null
                    ? `${run.num_turns}/${run.max_turns}`
                    : run.num_turns != null
                      ? String(run.num_turns)
                      : '—'}
                </td>
                <td>
                  {run.pr_url
                    ? <a href={run.pr_url} target="_blank" rel="noreferrer">PR</a>
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
